import { useEffect, useRef, useState } from "react";
import { Moon, Sun } from "lucide-react";
import { useTranslation } from "react-i18next";
import LogoMain from "./LogoMain";
import LanguageSwitcher from "./LanguageSwitcher";

const navItems = [
  { key: "home", href: "/" },
  { key: "products", href: "/products" },
  { key: "about", href: "/about" },
  { key: "blog", href: "/blog" },
  { key: "contact", href: "/contact" },
] as const;

const Header = () => {
  const { t } = useTranslation();
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [open, setOpen] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);

  /* ── theme bootstrap ──────────────────────────────────────── */
  useEffect(() => {
    const saved = localStorage.getItem("avoria-theme");
    if (saved) setIsDarkMode(saved === "dark");
    else setIsDarkMode(window.matchMedia("(prefers-color-scheme: dark)").matches);
  }, []);

  useEffect(() => {
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = (e: MediaQueryListEvent) => {
      if (!localStorage.getItem("avoria-theme")) setIsDarkMode(e.matches);
    };
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    if (isDarkMode) {
      root.classList.add("dark");
      localStorage.setItem("avoria-theme", "dark");
    } else {
      root.classList.remove("dark");
      localStorage.setItem("avoria-theme", "light");
    }
  }, [isDarkMode]);

  /* ── overlay: esc to close + scroll lock + focus trap ─────── */
  useEffect(() => {
    if (!open) return;
    const menuButton = menuButtonRef.current;
    const overlay = overlayRef.current;
    const getFocusable = () =>
      overlay
        ? Array.from(overlay.querySelectorAll<HTMLElement>('a[href], button:not([disabled])'))
        : [];

    getFocusable()[0]?.focus();

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
        return;
      }
      if (e.key === "Tab") {
        const items = getFocusable();
        if (items.length === 0) return;
        const first = items[0];
        const last = items[items.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
      menuButton?.focus();
    };
  }, [open]);

  return (
    <>
      <header className="sticky top-0 z-50 w-full bg-background border-b border-border">
        <div className="max-w-[1400px] mx-auto flex items-center justify-between px-6 py-4">
          <a href="/" aria-label="Avoria - home" className="flex items-center">
            <LogoMain />
          </a>

          <div className="flex items-center gap-3">
            <LanguageSwitcher />

            <span aria-hidden className="h-4 w-px bg-border" />

            <button
              onClick={() => setIsDarkMode((v) => !v)}
              aria-label={isDarkMode ? "Switch to light mode" : "Switch to dark mode"}
              className="inline-flex items-center justify-center w-10 h-10 rounded-full hover:bg-muted transition-colors duration-200"
            >
              {isDarkMode ? (
                <span className="h-4 w-4 inline-block">
                  <Sun size={16} />
                </span>
              ) : (
                <span className="h-4 w-4 inline-block">
                  <Moon size={16} />
                </span>
              )}
            </button>

            <button
              ref={menuButtonRef}
              onClick={() => setOpen(true)}
              aria-label="Open menu"
              aria-expanded={open}
              className="ed-eyebrow inline-flex items-center gap-2.5 px-4 py-2.5 rounded-full border border-foreground hover:bg-foreground hover:text-background transition-colors duration-200"
            >
              {t("common.menu")}
              <span className="flex flex-col gap-[3px]">
                <span className="block w-4 h-px bg-current" />
                <span className="block w-4 h-px bg-current" />
              </span>
            </button>
          </div>
        </div>
      </header>

      {/* ── Full-screen editorial menu overlay (always ink) ───── */}
      <div
        ref={overlayRef}
        role="dialog"
        aria-modal="true"
        className={`fixed inset-0 z-[60] flex flex-col bg-[hsl(30_8%_6%)] text-[hsl(44_15%_90%)] transition-[opacity,visibility] duration-300 ${
          open ? "opacity-100 visible" : "opacity-0 invisible"
        }`}
        aria-hidden={!open}
      >
        <div className="flex items-center justify-between px-6 py-4 w-full max-w-[1400px] mx-auto">
          <img src="/AvoriaLogo.svg" alt="Avoria" className="w-9 invert" />
          <button
            onClick={() => setOpen(false)}
            aria-label="Close menu"
            className="ed-eyebrow inline-flex items-center gap-2.5 px-4 py-2.5 rounded-full border border-white/30 hover:border-white/80 transition-colors duration-200"
          >
            {t("common.close")}
            <span className="relative block w-3.5 h-3.5">
              <span className="absolute left-0 right-0 top-1/2 h-px bg-current rotate-45" />
              <span className="absolute left-0 right-0 top-1/2 h-px bg-current -rotate-45" />
            </span>
          </button>
        </div>

        <nav className="flex-1 flex flex-col justify-center w-full max-w-[1400px] mx-auto px-6 overflow-y-auto">
          {navItems.map((item, i) => (
            <a
              key={item.href}
              href={item.href}
              onClick={() => setOpen(false)}
              className="group flex items-baseline gap-4 md:gap-6 py-1 border-b border-white/10"
            >
              <span className="ed-eyebrow text-white/30 w-7 shrink-0">0{i + 1}</span>
              <span className="ed-display leading-none transition-colors duration-200 group-hover:text-brand">
                {t(`nav.${item.key}`)}
              </span>
              <span className="ed-serif text-xl md:text-3xl text-white/40 group-hover:text-white/75 transition-colors duration-200">
                {t(`nav.${item.key}Sub`)}
              </span>
            </a>
          ))}
        </nav>

        <div className="flex items-center justify-between px-6 py-6 w-full max-w-[1400px] mx-auto ed-eyebrow text-white/50">
          <a href="mailto:contact@avoria.com" className="hover:text-white transition-colors duration-200">
            ( contact@avoria.com )
          </a>
          <div className="flex items-center gap-5">
            <LanguageSwitcher tone="overlay" />
            <span>( {t("common.builtInBrazil")} )</span>
          </div>
        </div>
      </div>
    </>
  );
};

export default Header;
